import { useState } from 'react';

import { useFormField } from '@american-autonomy/ag-ui';

import WithField from 'components/WithField';
import NotesInput from 'components/inputs/NotesInput';
import CaptionText from 'components/text/CaptionText';

import useTranslation from './hooks/useTranslation';

import { COUNTER_SEPARATOR, NOTES_MAX_CHARACTERS, TEST_ID } from './constants';
import type { FieldPath, FieldValues, HandleChange, Props, RegisterOptions } from './types';

import styles from './styles.module.scss';

const NotesField = <T extends FieldValues>(props: Props<T>) => {
    const {
        className = '',
        control,
        isDisabled,
        isEditable,
        isPlain,
        isRequired,
        name,
        placeholder,
        testId = TEST_ID
    } = props;

    const { NOTES_FIELD_LABEL } = useTranslation();

    const [validationRules, setValidationRules] = useState<RegisterOptions<T, FieldPath<T>>>({});

    const {
        field: { onBlur, onChange, value }
    } = useFormField({
        control,
        name,
        rules: validationRules
    });

    const handleInit = (rules: RegisterOptions) => {
        setValidationRules(rules as RegisterOptions<T, FieldPath<T>>);
    };

    const handleChange: HandleChange = notes => onChange(notes.slice(0, NOTES_MAX_CHARACTERS));

    const notesLength = (value ?? '').length;

    return (
        <WithField
            className={className}
            isPlain={isPlain}
            isRequired={isRequired}
            label={NOTES_FIELD_LABEL}
            testId={testId}>
            <NotesInput
                isDisabled={isDisabled}
                isEditable={isEditable}
                isRequired={isRequired}
                maxCharacters={NOTES_MAX_CHARACTERS}
                name={name as string}
                onBlur={onBlur}
                onChange={handleChange}
                onInit={handleInit}
                placeholder={placeholder}
                value={value}
            />

            <CaptionText
                className={styles.counter}
                text={`${notesLength}${COUNTER_SEPARATOR}${NOTES_MAX_CHARACTERS}`}
            />
        </WithField>
    );
};

export default NotesField;
